// SplitTicketModal.js
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Grid,
  Paper,
  Alert,
} from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DodajFaktury from "./DodajFaktury";
import DeleteIcon from "@mui/icons-material/Delete";
import { submitSplitData } from "./functions";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const SplitTicketModal = ({ open, onClose, ticketData }) => {
  const [faktury, setFaktury] = useState([]);
  const [liczbaOdpraw, setLiczbaOdpraw] = useState("2");
  const [podzial, setPodzial] = useState({});
  const [readyToDrag, setReadyToDrag] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [selectedFaktury, setSelectedFaktury] = useState([]);
  const navigate = useNavigate();
  const liczbaOdprawParsed = parseInt(liczbaOdpraw, 10);
  const isLiczbaOdprawValid =
    !isNaN(liczbaOdprawParsed) && liczbaOdprawParsed >= 2;

  useEffect(() => {
    if (ticketData?.nrFaktury) {
      const parsed = ticketData.nrFaktury
        .split(",")
        .map((f) => f.trim())
        .filter(Boolean);
      setFaktury(parsed);
    }
  }, [ticketData]);

  const handleGenerate = () => {
    if (!isLiczbaOdprawValid) return;

    const nowyPodzial = { unassigned: [...faktury] };
    for (let i = 1; i <= liczbaOdprawParsed; i++) {
      nowyPodzial[`odprawa-${i}`] = [];
    }
    setPodzial(nowyPodzial);
    setReadyToDrag(true);
  };

  const handleSelectFaktura = (faktura, e) => {
    if (e.ctrlKey || e.metaKey) {
      setSelectedFaktury((prev) =>
        prev.includes(faktura)
          ? prev.filter((f) => f !== faktura)
          : [...prev, faktura]
      );
    } else {
      setSelectedFaktury([faktura]);
    }
  };

  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;

    const sourceList = [...podzial[source.droppableId]];
    const destList = [...podzial[destination.droppableId]];

    const fakturyDoPrzeniesienia = selectedFaktury.includes(draggableId)
      ? selectedFaktury
      : [draggableId];

    const noweSourceList = sourceList.filter(
      (f) => !fakturyDoPrzeniesienia.includes(f)
    );
    const noweDestList = [...destList];

    fakturyDoPrzeniesienia.forEach((f, i) => {
      noweDestList.splice(destination.index + i, 0, f);
    });

    setPodzial({
      ...podzial,
      [source.droppableId]: noweSourceList,
      [destination.droppableId]: noweDestList,
    });
    setSelectedFaktury([]);
  };

  const handleDeleteFaktura = (fakturaToDelete) => {
    setFaktury((prev) => prev.filter((f) => f !== fakturaToDelete));
    if (readyToDrag) {
      const updatedPodzial = {};
      for (const [key, list] of Object.entries(podzial)) {
        updatedPodzial[key] = list.filter((f) => f !== fakturaToDelete);
      }
      setPodzial(updatedPodzial);
    }
  };

  const handleSubmit = async () => {
    const unassigned = podzial["unassigned"] || [];
    const splits = Object.entries(podzial)
      .filter(([key]) => key !== "unassigned")
      .map(([key, fakturyGrupy]) => ({
        nrFaktury: fakturyGrupy,
        odprawaId: key,
      }));

    const hasEmptyGroup = splits.some((split) => split.nrFaktury.length === 0);
    if (unassigned.length > 0) {
      setValidationError("Nie wszystkie faktury zostały przypisane.");
      return;
    }
    if (hasEmptyGroup) {
      setValidationError(
        "Każda odprawa musi zawierać przynajmniej jedną fakturę."
      );
      return;
    }
    setValidationError("");

    try {
      await submitSplitData({
        originalTicketId: ticketData.id,
        nrRef: ticketData.nrRef,
        splits,
        ticketData,
      });
      toast.success("Ticket podzielony pomyślnie!", {
        position: "top-right",
        autoClose: 3000,
      });
      handleClose();
      navigate("/user");
    } catch (error) {
      console.error("Błąd zapisu danych:", error);
      toast.error("Wystąpił błąd podczas dzielenia ticketu.", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleClose = () => {
    setFaktury([]);
    setPodzial({});
    setReadyToDrag(false);
    setLiczbaOdpraw("2");
    setSelectedFaktury([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Podział ticketa</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1">
          <strong>Nr Ref:</strong> {ticketData.nrRef}
        </Typography>

        {!readyToDrag && faktury.length > 0 && (
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {faktury.map((faktura, i) => (
              <Grid item key={i}>
                <Paper
                  variant="outlined"
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "16px",
                    backgroundColor: "#f0f0f0",
                    display: "flex",
                    gap: 1,
                  }}
                >
                  <Typography>{faktura}</Typography>
                  <Button
                    onClick={() => handleDeleteFaktura(faktura)}
                    color="error"
                    size="small"
                    sx={{ minWidth: 0, padding: 0 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        <DodajFaktury
          onSave={(nowe) => {
            const updatedFaktury = [...faktury, ...nowe];
            setFaktury(updatedFaktury);
            if (readyToDrag) {
              setPodzial((prev) => ({
                ...prev,
                unassigned: [...(prev.unassigned || []), ...nowe],
              }));
            }
          }}
        />

        {!readyToDrag ? (
          <>
            <TextField
              label="Na ile ticketów chcesz podzielić?"
              value={liczbaOdpraw}
              onChange={(e) => setLiczbaOdpraw(e.target.value)}
              fullWidth
              sx={{ my: 2 }}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*", min: 2 }}
            />
            {!isLiczbaOdprawValid && (
              <Typography variant="caption" color="error">
                Podział musi mieć co najmniej 2 odprawy.
              </Typography>
            )}
            <Button
              onClick={handleGenerate}
              variant="contained"
              color="primary"
              disabled={!isLiczbaOdprawValid}
            >
              Generuj podział
            </Button>
          </>
        ) : (
          <>
            <Typography variant="subtitle1" gutterBottom>
              Przeciągnij faktury do poszczególnych odpraw. Możesz znaznaczyć
              wiele faktur na raz za pomocą przycisku Control (Ctrl) na
              klawiaturze
            </Typography>
            {validationError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {validationError}
              </Alert>
            )}
            <DragDropContext onDragEnd={onDragEnd}>
              <Grid container spacing={2}>
                {Object.entries(podzial).map(([key, fakturyList]) => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Droppable droppableId={key}>
                      {(provided) => (
                        <Paper
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          sx={{ p: 2, minHeight: 150 }}
                        >
                          <Typography variant="subtitle2">
                            {key === "unassigned"
                              ? "Nieprzypisane faktury"
                              : `Odprawa ${key.split("-")[1]}`}
                          </Typography>
                          {fakturyList.map((faktura, index) => (
                            <Draggable
                              key={faktura}
                              draggableId={faktura}
                              index={index}
                            >
                              {(provided) => (
                                <Paper
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  onClick={(e) =>
                                    handleSelectFaktura(faktura, e)
                                  }
                                  sx={{
                                    p: 1,
                                    my: 1,
                                    backgroundColor: selectedFaktury.includes(
                                      faktura
                                    )
                                      ? "#cceeff"
                                      : "white",
                                    cursor: "pointer",
                                  }}
                                >
                                  <Typography variant="body2">
                                    {faktura}
                                  </Typography>
                                  <Button
                                    onClick={() => handleDeleteFaktura(faktura)}
                                    color="error"
                                    size="small"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </Button>
                                </Paper>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </Paper>
                      )}
                    </Droppable>
                  </Grid>
                ))}
              </Grid>
            </DragDropContext>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Anuluj
        </Button>
        {readyToDrag && (
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Podziel ticket
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SplitTicketModal;
